import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AWARENESS_LEVELS = [
  {
    value: 'Unaware',
    label: 'Unaware',
    description: "Don't know they have a problem"
  },
  {
    value: 'Problem Aware',
    label: 'Problem Aware',
    description: 'Know the problem, not the solutions'
  },
  {
    value: 'Solution Aware',
    label: 'Solution Aware',
    description: 'Know solutions exist, exploring options'
  },
  {
    value: 'Product Aware',
    label: 'Product Aware',
    description: 'Know your solution, need convincing'
  },
  {
    value: 'Most Aware',
    label: 'Most Aware',
    description: 'Past customers or warm referrals'
  }
];

function EmailCampaignBuilder() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [profiles, setProfiles] = useState([]);
  
  const [formData, setFormData] = useState({
    profile_id: '',
    offer_name: '',
    offer_type: 'Service',
    price_point: '',
    primary_benefit: '',
    awareness_level: 'Problem Aware',
    target_audience: '',
    primary_pain_point: '',
    proof_element: ''
  });

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/email-campaigns/profiles`);
      const profilesList = response.data.profiles || [];
      setProfiles(profilesList);
      
      if (profilesList.length === 0) {
        toast.error('Please create a business profile first');
        navigate('/email-campaigns/profile');
      } else if (profilesList.length === 1) {
        setFormData(prev => ({ ...prev, profile_id: profilesList[0].profile_id }));
      }
    } catch (error) {
      console.error('Load profiles error:', error);
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const nextStep = () => {
    if (step === 1 && !formData.profile_id) {
      toast.error('Please select a business profile');
      return;
    }
    if (step === 2 && (!formData.offer_name || !formData.primary_benefit || !formData.price_point)) {
      toast.error('Please fill in all offer fields');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleGenerate = async () => {
    if (!formData.target_audience || !formData.primary_pain_point || !formData.proof_element) {
      toast.error('Please fill in all audience fields');
      return;
    }

    setGenerating(true);

    try {
      const response = await axios.post(`${API}/email-campaigns/generate`, formData);
      toast.success('Email sequence generated!');
      navigate(`/email-campaigns/${response.data.campaign_id}`);
    } catch (error) {
      console.error('Generate error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate campaign');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4aa] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/email-campaigns')}
          className="mb-4 text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h1 className="text-3xl font-bold text-white mb-2">
          Create Email Sequence
        </h1>
        <p className="text-white/60">
          Generate a 4-email direct response sequence
        </p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8 flex items-center justify-center gap-4">
        {[1, 2, 3].map((s) => (
          <React.Fragment key={s}>
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step >= s
                  ? 'bg-[#00d4aa] text-black'
                  : 'bg-white/10 text-white/40'
              }`}
            >
              {s}
            </div>
            {s < 3 && (
              <div
                className={`h-1 w-12 ${
                  step > s ? 'bg-[#00d4aa]' : 'bg-white/10'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <Card className="card">
        <CardHeader>
          <CardTitle>
            {step === 1 && 'Step 1: Select Business Profile'}
            {step === 2 && 'Step 2: Define Your Offer'}
            {step === 3 && 'Step 3: Define Your Audience'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1: Profile Selection */}
          {step === 1 && (
            <>
              <div>
                <Label htmlFor="profile_id" className="text-white mb-2">
                  Business Profile *
                </Label>
                <select
                  id="profile_id"
                  name="profile_id"
                  value={formData.profile_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d4aa]"
                >
                  <option value="">Select a profile...</option>
                  {profiles.map((profile) => (
                    <option key={profile.profile_id} value={profile.profile_id}>
                      {profile.business_name} ({profile.industry})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                <p className="text-white/80 text-sm">
                  <strong className="text-purple-400">Tip:</strong> Your business profile contains the sender name, industry, and tone that will be used throughout the email sequence.
                </p>
              </div>
              
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/email-campaigns/profile')}
                className="w-full"
              >
                Create New Profile
              </Button>
            </>
          )}

          {/* Step 2: Offer Details */}
          {step === 2 && (
            <>
              <div>
                <Label htmlFor="offer_name" className="text-white mb-2">
                  Offer Name *
                </Label>
                <Input
                  id="offer_name"
                  name="offer_name"
                  value={formData.offer_name}
                  onChange={handleChange}
                  placeholder="e.g., Delta Growth Workshop"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <Label htmlFor="offer_type" className="text-white mb-2">
                  Offer Type *
                </Label>
                <select
                  id="offer_type"
                  name="offer_type"
                  value={formData.offer_type}
                  onChange={handleChange}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00d4aa]"
                >
                  <option value="Service">Service</option>
                  <option value="Product">Product</option>
                  <option value="Event">Event</option>
                  <option value="Consultation">Consultation</option>
                  <option value="Workshop">Workshop</option>
                </select>
              </div>

              <div>
                <Label htmlFor="price_point" className="text-white mb-2">
                  Price Point *
                </Label>
                <Input
                  id="price_point"
                  name="price_point"
                  value={formData.price_point}
                  onChange={handleChange}
                  placeholder="e.g., $500, $2,997, Free"
                  className="bg-white/5 border-white/10 text-white"
                />
              </div>

              <div>
                <Label htmlFor="primary_benefit" className="text-white mb-2">
                  Primary Benefit *
                </Label>
                <Textarea
                  id="primary_benefit"
                  name="primary_benefit"
                  value={formData.primary_benefit}
                  onChange={handleChange}
                  placeholder="e.g., Generate qualified leads for under $50 each"
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                />
                <p className="text-white/40 text-sm mt-1">
                  What&apos;s the main transformation or result they&apos;ll get?
                </p>
              </div>
            </>
          )}

          {/* Step 3: Audience Details */}
          {step === 3 && (
            <>
              <div>
                <Label htmlFor="awareness_level" className="text-white mb-2">
                  Audience Awareness Level *
                </Label>
                <div className="space-y-2">
                  {AWARENESS_LEVELS.map((level) => (
                    <label
                      key={level.value}
                      className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                        formData.awareness_level === level.value
                          ? 'bg-[#00d4aa]/10 border-[#00d4aa]/50'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <input
                        type="radio"
                        name="awareness_level"
                        value={level.value}
                        checked={formData.awareness_level === level.value}
                        onChange={handleChange}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-white font-medium">{level.label}</div>
                        <div className="text-white/60 text-sm">{level.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="target_audience" className="text-white mb-2">
                  Target Audience *
                </Label>
                <Textarea
                  id="target_audience"
                  name="target_audience"
                  value={formData.target_audience}
                  onChange={handleChange}
                  placeholder="e.g., Electrical contractors in Brisbane with 2-10 employees"
                  className="bg-white/5 border-white/10 text-white"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="primary_pain_point" className="text-white mb-2">
                  Primary Pain Point *
                </Label>
                <Textarea
                  id="primary_pain_point"
                  name="primary_pain_point"
                  value={formData.primary_pain_point}
                  onChange={handleChange}
                  placeholder="e.g., Inconsistent lead flow and wasted ad spend"
                  className="bg-white/5 border-white/10 text-white"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="proof_element" className="text-white mb-2">
                  Proof Element (Case Study/Testimonial) *
                </Label>
                <Textarea
                  id="proof_element"
                  name="proof_element"
                  value={formData.proof_element}
                  onChange={handleChange}
                  placeholder="e.g., Dave, a Brisbane electrician, reduced his cost per lead from $448 to $47 in 3 weeks"
                  className="bg-white/5 border-white/10 text-white"
                  rows={3}
                />
                <p className="text-white/40 text-sm mt-1">
                  This will be used as social proof in your emails
                </p>
              </div>
            </>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-4 border-t border-white/10">
            {step > 1 && (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            )}
            {step < 3 ? (
              <Button
                type="button"
                onClick={nextStep}
                className="flex-1 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="flex-1 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Sequence
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default EmailCampaignBuilder;
